import { http } from '@/utils/api'
import { withPbx } from '@/stores/network/api'
import { useAuthStore } from '@/stores/auth/store'
import * as Sentry from '@/core/sentry'
import { transformIncomingCta } from '@/utils/dataTransformers'
import type { ColorName } from '@/utils/colors'
import { isWhitelistError } from '@/utils/api/whitelist'

export type GroupTag = {
  id: number
  color: ColorName
  label: string
  assignedInCallflows: number[]
  assignedToDevice: boolean
}

export type Pbx = {
  id: number
  maxLines: number
  maxDevices: number
  expertMode: boolean
  director: string
  adminMail: string
  adminPhone: null | string
  createdAt: string
  updatedAt: string
  showVoiptogoofficedesks?: boolean
  normalisePhonebook?: boolean
  canSwitchDirector: boolean
  customer: number
  hasTcProduct: boolean
  /** DEPRECATED: use the "cta.connectme" feature flag */
  hasConnectMeProduct: boolean
  isTcxImportEnabled?: boolean
  betaAccess: boolean
  currentTariff: string
  upgradeTariff: string
  downgradeTariff: string
  edition: 'basic' | 'standard' | 'pro'
  serviceCountry?: string
  analyticsEnabled: boolean
  analyticsDpaConsented: string | null
  displayName: string
  language: string
  trunks: {
    id: number
    username: string
    label: string
    showAsTrunkAndSingleNumber: boolean
    isBlockNumber: boolean
    isPartialBlock: boolean
    blockRangeStart: number
    blockRangeEnd: number
    blockSize: number
    customer: number
    phoneNumber?: string
    e164InternationalPrefix: string
  }[]
  tapiConnection: {
    id: number
    ips: string[]
    password: string
    createdAt: string
    updatedAt: string
    pbxId: number
    username: string
  }
  catchAllExtension: {
    id: number
    name: string
    trunkId: number
    type: string
    quickDial: string
    isMsn: boolean
    color: null
    privacy: boolean
    dnd: boolean
    email: string
    smsNumber: null
    forwardingType: string
    showMissedCalls: boolean
    isClosingActive: boolean
    calleridForwardType: string
    conference: null
    e164: string
    setNameAsPrefix: boolean
    directDialing: string
    editable: boolean
    extendedForwardingProfiles: []
    devices: []
    forwardings: []
    closing: null
    permissionsToEdit: []
  }
  groupTags: GroupTag[]
  // Explicitly allowed outgoing CLIs (international format, e.g. "4312345678")
  allowedClis?: string[]
  // Object with label as key and URL as value
  specialButtons?: Record<string, string>
  /** Specifies if the user has clicked Yes or No on the entra suggestion popup: null = initial, true = Yes, false = No */
  configuredWithEntra: null | boolean
}

export async function loadCtaSettings() {
  try {
    const ctaId = useAuthStore().ctaId

    const url = ctaId ? `pbxes/${ctaId}` : 'pbxes'

    const { data } = await http.get<Pbx[] | Pbx>(url)

    return {
      status: 'success' as const,
      payload: transformIncomingCta(data),
    }
  } catch (error: any) {
    Sentry.captureException(error)

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

export async function updateCtaSettings(ctaSettings: any) {
  try {
    const ctaId = useAuthStore().ctaId

    const url = ctaId ? `pbxes/${ctaId}` : 'pbxes'

    const { data } = await http.put<Pbx>(url, ctaSettings)

    return {
      status: 'success' as const,
      payload: transformIncomingCta(data),
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

export async function resetCta() {
  try {
    const url = withPbx('reset')

    const { data } = await http.put<{}>(url, {})

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

export async function restartCta() {
  try {
    const url = withPbx('reboot')

    const { data } = await http.put<{}>(url, {})

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}
